import { useGetIdentity, useLogout, useNotification } from "@refinedev/core";
import { Button, Card, Descriptions, Space, Typography } from "antd";
import dayjs from "dayjs";
import { useState, type FC } from "react";
import { axiosInstance } from "../../api/client";
import { PlanTag } from "../../components/plan-tag";
import { PROJECT_LIMIT } from "../../components/plan-tag/constants";
import type { User } from "../../types";

const { Title } = Typography;

export const Settings: FC = () => {
  const { data: identity, isLoading } = useGetIdentity<User>();
  const { mutate: logout } = useLogout();
  const { open: notify } = useNotification();
  const [billingLoading, setBillingLoading] = useState(false);

  const handleUpgrade = async () => {
    setBillingLoading(true);
    try {
      const priceId = import.meta.env.VITE_STRIPE_PRO_PRICE_ID ?? "";
      const { data } = await axiosInstance.post<{ url: string }>("/billing/checkout", { priceId });
      window.location.href = data.url;
    } catch {
      notify?.({
        type: "error",
        message: "Billing error",
        description: "Could not start the checkout session. Please try again.",
      });
      setBillingLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setBillingLoading(true);
    try {
      const { data } = await axiosInstance.post<{ url: string }>("/billing/portal");
      window.location.href = data.url;
    } catch {
      notify?.({
        type: "error",
        message: "Billing error",
        description: "Could not open the billing portal. Please try again.",
      });
      setBillingLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 600, margin: "0 auto", padding: "24px 0" }}>
      <Title level={4}>Settings</Title>
      <Card loading={isLoading}>
        <Descriptions column={1} bordered>
          <Descriptions.Item label="Email">{identity?.email}</Descriptions.Item>
          <Descriptions.Item label="Plan">
            {identity?.plan && <PlanTag plan={identity.plan} />}
          </Descriptions.Item>
          <Descriptions.Item label="CI deploy projects">
            {identity?.plan ? PROJECT_LIMIT[identity.plan] : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Member since">
            {identity?.createdAt
              ? dayjs(identity.createdAt).format("MMMM D, YYYY")
              : "-"}
          </Descriptions.Item>
        </Descriptions>
        <Space style={{ marginTop: 24 }}>
          {identity?.plan === "free" && (
            <Button type="primary" loading={billingLoading} onClick={handleUpgrade}>
              Upgrade to Pro
            </Button>
          )}
          {(identity?.plan === "pro" || identity?.plan === "agency") && (
            <Button loading={billingLoading} onClick={handleManageSubscription}>
              Manage subscription
            </Button>
          )}
          <Button danger onClick={() => logout()}>
            Sign out
          </Button>
        </Space>
      </Card>
    </div>
  );
};
